<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">{{ pageTitle }}</h1>
      </div>
      <div class="header-actions">
        <button class="btn btn-primary" @click="handleCreate">
                <i class="ri-add-line"></i>
                新增標籤
              </button>
      </div>
    </div>

    <div class="page-container">
    <details class="tag-guide">
      <summary>
        <i class="ri-information-line"></i>
        <span class="tag-guide-title">標籤來源與類型說明</span>
        <span class="tag-guide-hint">查看每個項目的用途</span>
        <i class="ri-arrow-down-s-line tag-guide-chevron"></i>
      </summary>
      <div class="tag-guide-body">
        <section>
          <h2>標籤來源</h2>
          <ul>
            <li>
              <strong>互動標籤</strong>
              <span>手動建立並貼上。可以編輯、刪除。同一會員重複貼上時會累加濃度。</span>
            </li>
            <li>
              <strong>自動規則標籤</strong>
              <span>由自動貼標規則產生，例如新會員、沈睡客、高價值顧客。可在此頁編輯名稱、顏色、圖示與說明，不能設為限時標籤；修改名稱會同步到對應規則。條件請到「自動貼標規則」調整。此頁不能刪除。</span>
            </li>
            <li>
              <strong>專屬標籤</strong>
              <span>目前用於門市。建立門市時自動產生，名稱為「store_門市名稱」，並對應該門市。可改顏色、圖示與說明，不能改名稱，也不能設為限時標籤；刪除請到門市管理。</span>
            </li>
          </ul>
        </section>
        <section>
          <h2>標籤類型</h2>
          <ul>
            <li>
              <strong>一般標籤</strong>
              <span>沒有到期日，會一直保留，直到手動移除或由規則移除。</span>
            </li>
            <li>
              <strong>限時標籤</strong>
              <span>有到期時間，到期後不再視為有效。過期方式可以是指定天數後、固定日期、月底或季末。</span>
            </li>
          </ul>
        </section>
      </div>
    </details>

    <div v-if="builtInStatus && !builtInStatus.complete" class="built-in-banner">
      <div class="built-in-banner-text">
        <strong>系統內建標籤尚未齊全</strong>
        <p>缺少：{{ missingBuiltInNames }}。建立後會一併補上對應的自動貼標規則。</p>
      </div>
      <button class="btn btn-primary" :disabled="creatingBuiltIn" @click="handleCreateBuiltIn">
        <i class="ri-add-line"></i>
        {{ creatingBuiltIn ? '建立中...' : '建立缺少的標籤' }}
      </button>
    </div>

    <div class="page-toolbar">
      <div class="filter-group">
        <select v-model="filterActive" class="form-control" @change="handleFilterChange">
          <option value="">全部狀態</option>
          <option value="true">啟用</option>
          <option value="false">停用</option>
        </select>
      </div>
      <div class="filter-group">
        <select v-model="filterSource" class="form-control" @change="handleFilterChange">
          <option value="">全部來源</option>
          <option value="manual">互動標籤</option>
          <option value="auto_rule">自動規則標籤</option>
          <option value="exclusive">專屬標籤</option>
        </select>
      </div>
      <div class="filter-group">
        <select v-model="filterTemporary" class="form-control" @change="handleFilterChange">
          <option value="">全部類型</option>
          <option value="true">限時標籤</option>
          <option value="false">一般標籤</option>
        </select>
      </div>
    </div>

    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>載入中...</p>
    </div>

    <div v-else-if="error" class="error-state">
      <p class="error-message">{{ error }}</p>
      <button class="btn btn-secondary" @click="fetchTags">重試</button>
    </div>

    <div v-else class="table-wrapper">
      <table>
        <thead>
          <tr>
            <th>標籤名稱</th>
            <th>顏色</th>
            <th>來源</th>
            <th>說明</th>
            <th>限時標籤</th>
            <th>狀態</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="tags.length === 0">
            <td colspan="7" class="empty-state">
              沒有找到標籤
            </td>
          </tr>
          <tr v-for="tag in tags" :key="tag.id">
            <td>
              <div class="tag-name-cell">
                <span v-if="tag.tagIcon" class="tag-icon">
                  <i :class="tag.tagIcon"></i>
                </span>
                <span v-if="tag.tagColor" class="tag-name" :style="{ color: tag.tagColor }">
                  {{ tag.tagName }}
                </span>
                <span v-else class="tag-name">{{ tag.tagName }}</span>
              </div>
            </td>
            <td>
              <span v-if="tag.tagColor" class="color-badge" :style="{ backgroundColor: tag.tagColor }"></span>
              <span v-else>-</span>
            </td>
            <td>
              <span class="badge badge-info">{{ getSourceText(tag.tagSource) }}</span>
            </td>
            <td>{{ tag.description || '-' }}</td>
            <td>
              <span v-if="tag.isTemporary" class="badge badge-warning">
                <i class="ri-time-line"></i> 限時
                <span v-if="tag.expiryRule" class="expiry-info">
                  ({{ getExpiryRuleText(tag.expiryRule) }})
                </span>
              </span>
              <span v-else class="text-muted">-</span>
            </td>
            <td>
              <span :class="tag.isActive ? 'badge badge-success' : 'badge badge-secondary'">
                {{ tag.isActive ? '啟用' : '停用' }}
              </span>
            </td>
            <td>
              <div class="action-buttons">
                <button 
                  class="btn btn-sm btn-secondary" 
                  @click="handleEdit(tag.id)"
                  :disabled="tag.tagSource === 'system'"
                  :title="tag.tagSource === 'system' ? '系統標籤無法編輯' : (tag.tagSource === 'exclusive' && tag.tagName?.startsWith('store_') ? '門市標籤可編輯，但無法修改名稱' : '')"
                >
                  <i class="ri-edit-line"></i>
                  編輯
                </button>
                <button 
                  v-if="tag.tagSource === 'manual'"
                  class="btn btn-sm btn-danger" 
                  @click="handleDelete(tag.id)"
                >
                  <i class="ri-delete-bin-line"></i>
                  刪除
                </button>
                <span 
                  v-else-if="tag.tagSource === 'exclusive' && tag.tagName?.startsWith('store_')"
                  class="text-muted"
                  title="門市標籤無法刪除，請至門市管理進行操作"
                >
                  -
                </span>
                <span 
                  v-else
                  class="text-muted"
                  :title="tag.tagSource === 'system' ? '系統標籤無法刪除' : '自動規則標籤無法刪除'"
                >
                  -
                </span>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { usePageTitle } from '@/composables/usePageTitle.js';
import { tagService } from '@/api/modules/tags.js';
import { showError, showDeleteConfirm, showSuccess, showConfirm } from '@/utils/swal.js';

export default {
  name: 'ClientTags',
  setup() {
    const router = useRouter();
    const { pageTitle } = usePageTitle('標籤管理');
    const tags = ref([]);
    const loading = ref(false);
    const error = ref(null);
    const filterActive = ref('');
    const filterSource = ref('');
    const filterTemporary = ref('');
    const builtInStatus = ref(null);
    const creatingBuiltIn = ref(false);
    const missingBuiltInNames = computed(() => (
      (builtInStatus.value?.missing || []).map((item) => item.tagName).join('、')
    ));

    const fetchTags = async () => {
      loading.value = true;
      error.value = null;

      try {
        const params = {};
        if (filterActive.value !== '') {
          params.isActive = filterActive.value === 'true';
        }
        if (filterSource.value !== '') {
          params.tagSource = filterSource.value;
        }
        if (filterTemporary.value !== '') {
          params.isTemporary = filterTemporary.value === 'true';
        }

        const response = await tagService.getTags(params);

        if (response && response.success && response.data) {
          tags.value = (response.data || []).filter(tag => tag.tagSource !== 'system');
        } else {
          error.value = response?.message || '獲取標籤列表失敗';
        }
      } catch (err) {
        console.error('獲取標籤列表失敗:', err);
        error.value = err.message || '獲取標籤列表失敗';
        await showError('載入失敗', error.value);
      } finally {
        loading.value = false;
      }
    };

    const fetchBuiltInStatus = async () => {
      try {
        const response = await tagService.getBuiltInStatus();
        if (response && response.success && response.data) {
          builtInStatus.value = response.data;
        }
      } catch (err) {
        console.error('檢查系統內建標籤失敗:', err);
      }
    };

    const handleCreateBuiltIn = async () => {
      creatingBuiltIn.value = true;
      try {
        const response = await tagService.createBuiltInTags();
        if (response && response.success && response.data) {
          builtInStatus.value = response.data;
          await fetchTags();
          if (response.data.complete) {
            await showSuccess('建立成功', '缺少的系統內建標籤與規則已建立');
          } else {
            const names = (response.data.missing || []).map((item) => item.tagName).join('、');
            await showError('尚未全部建立', response.message || `仍缺少：${names}`);
          }
        } else {
          await showError('建立失敗', response?.message || '建立系統內建標籤失敗');
        }
      } catch (err) {
        console.error('建立系統內建標籤失敗:', err);
        await showError('建立失敗', err.response?.data?.message || err.message || '建立系統內建標籤失敗');
      } finally {
        creatingBuiltIn.value = false;
      }
    };

    const handleFilterChange = () => {
      fetchTags();
    };

    const handleCreate = () => {
      router.push('/client/tags/create');
    };

    const handleEdit = (tagId) => {
      router.push(`/client/tags/${tagId}/edit`);
    };

    const handleDelete = async (tagId) => {
      const tag = tags.value.find(t => t.id === tagId);
      const tagName = tag ? tag.tagName : '此標籤';

      try {
        // 先檢查是否有會員關聯資料
        const countResponse = await tagService.getMemberTagCount(tagId);
        const memberCount = countResponse?.data?.count || 0;

        let confirmed = false;
        if (memberCount > 0) {
          // 如果有會員關聯，顯示警告確認對話框
          confirmed = await showConfirm(
            '確認刪除標籤',
            `標籤「${tagName}」目前有 ${memberCount} 位會員使用此標籤。\n\n刪除標籤後，所有會員的此標籤關聯資料也會一併刪除。\n\n確定要刪除嗎？`,
            '確定刪除',
            '取消',
            'warning'
          );
        } else {
          // 如果沒有會員關聯，使用一般的刪除確認
          confirmed = await showDeleteConfirm(tagName);
        }

        if (!confirmed) return;

        // 執行刪除
        const response = await tagService.deleteTag(tagId);

        if (response && response.success) {
          await showSuccess('刪除成功', memberCount > 0 ? `標籤已刪除，並已移除 ${memberCount} 位會員的標籤關聯` : '標籤已刪除');
          await fetchTags();
        } else {
          await showError('刪除失敗', response?.message || '刪除標籤失敗');
        }
      } catch (err) {
        console.error('刪除標籤失敗:', err);
        const errorMessage = err.response?.data?.message || err.message || '刪除標籤失敗';
        await showError('刪除失敗', errorMessage);
      }
    };

    const getSourceText = (source) => {
      const sourceMap = {
        manual: '互動標籤',
        auto_rule: '自動規則標籤',
        system: '系統標籤',
        exclusive: '專屬標籤'
      };
      return sourceMap[source] || source;
    };

    const getExpiryRuleText = (expiryRule) => {
      const ruleMap = {
        days_from_assign: '指定天數後過期',
        fixed_date: '固定日期過期',
        end_of_month: '月底過期',
        end_of_quarter: '季末過期'
      };
      return ruleMap[expiryRule] || expiryRule;
    };

    onMounted(() => {
      fetchTags();
      fetchBuiltInStatus();
    });

    return {
      tags,
      loading,
      error,
      pageTitle,
      filterActive,
      filterSource,
      filterTemporary,
      builtInStatus,
      creatingBuiltIn,
      missingBuiltInNames,
      fetchTags,
      handleFilterChange,
      handleCreate,
      handleCreateBuiltIn,
      handleEdit,
      handleDelete,
      getSourceText,
      getExpiryRuleText,
    };
  },
};
</script>

<style scoped>
@import '~@/assets/scss/components/_list-page-common.scss';

.built-in-banner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
  padding: 14px 16px;
  background: #fff7ed;
  border: 1px solid #fdba74;
  border-left: 4px solid #ea580c;
  border-radius: 8px;
  color: #9a3412;
}

.built-in-banner-text strong {
  display: block;
  margin-bottom: 4px;
  font-size: 15px;
}

.built-in-banner-text p {
  margin: 0;
  font-size: 13px;
  line-height: 1.6;
}

.built-in-banner .btn {
  flex-shrink: 0;
}

.tag-guide {
  margin-bottom: 16px;
  background: #eff6ff;
  border: 1px solid #93c5fd;
  border-left: 4px solid #2563eb;
  border-radius: 8px;
  color: #1e3a8a;
}

.tag-guide summary {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 14px 16px;
  cursor: pointer;
  list-style: none;
  font-size: 15px;
}

.tag-guide summary::-webkit-details-marker {
  display: none;
}

.tag-guide summary > .ri-information-line {
  font-size: 20px;
  color: #2563eb;
  flex-shrink: 0;
}

.tag-guide-title {
  font-weight: 700;
}

.tag-guide-hint {
  color: #3b82f6;
  font-size: 13px;
}

.tag-guide-chevron {
  margin-left: auto;
  font-size: 20px;
  transition: transform 0.15s ease;
}

.tag-guide[open] .tag-guide-chevron {
  transform: rotate(180deg);
}

.tag-guide-body {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  padding: 0 16px 16px;
}

.tag-guide-body h2 {
  margin: 0 0 8px;
  font-size: 14px;
  font-weight: 700;
}

.tag-guide-body ul {
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.tag-guide-body li {
  background: #fff;
  border: 1px solid #dbeafe;
  border-radius: 6px;
  padding: 10px 12px;
}

.tag-guide-body strong {
  display: block;
  margin-bottom: 4px;
  color: #1d4ed8;
}

.tag-guide-body span {
  display: block;
  font-size: 13px;
  line-height: 1.6;
  color: #1e3a8a;
}

@media (max-width: 768px) {
  .built-in-banner {
    flex-direction: column;
    align-items: stretch;
  }

  .tag-guide summary {
    flex-wrap: wrap;
  }

  .tag-guide-hint {
    flex-basis: 100%;
    margin-left: 28px;
  }

  .tag-guide-body {
    grid-template-columns: 1fr;
  }
}

/* 覆寫表格最小寬度（7 個欄位需要更大的寬度） */
.table-wrapper table {
  min-width: 900px;
}

thead {
  background-color: #f5f5f5;
}

th,
td {
  padding: 12px;
  text-align: left;
  border-bottom: 1px solid #e0e0e0;
}

th {
  font-weight: 600;
  color: #333;
}

tbody tr:hover {
  background-color: #f9f9f9;
}

.empty-state {
  text-align: center;
  color: #999;
  padding: 40px;
}

.tag-name-cell {
  display: flex;
  align-items: center;
  gap: 8px;
}

.tag-icon {
  font-size: 18px;
  color: #666;
  display: flex;
  align-items: center;
}

.tag-name {
  font-weight: 600;
}

.color-badge {
  display: inline-block;
  width: 24px;
  height: 24px;
  border-radius: 4px;
  border: 1px solid #ddd;
}

.icon-badge {
  font-size: 18px;
}

.action-buttons {
  display: flex;
  gap: 8px;
}

.btn-sm {
  padding: 6px 12px;
  font-size: 14px;
}

.loading-state,
.error-state {
  text-align: center;
  padding: 40px;
}

.error-state .error-message {
  color: #dc3545;
  margin-bottom: 12px;
}

.badge-warning {
  background-color: #ffc107;
  color: #000;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.expiry-info {
  font-size: 11px;
  opacity: 0.8;
  margin-left: 4px;
}
</style>
